// Offline-first call history hook.
//
// Read path:  useLiveQuery on `remote_call_history` (Dexie) → reactive,
//             renders immediately from cache on every subsequent visit.
// Write path: background network fetch on mount → bulkPut into Dexie →
//             liveQuery re-emits automatically.
//
// isLoading is only true when:
//   • Dexie hasn't emitted yet (< 1ms on first mount), OR
//   • the cache is empty AND the network fetch is still in-flight.
// Once the user has seen /calls at least once, the page renders
// instantly from cache and refreshes silently behind the scenes.

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import { log } from '@core/log/log';
import {
  type CallHistoryEntry,
  type CallKind,
  loadCallHistory,
} from '@core/network/calls';
import { db, type RemoteCallHistoryRow } from '@core/persistence/db';

interface UseCallHistoryResult {
  readonly calls: readonly CallHistoryEntry[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly refresh: () => Promise<void>;
  readonly startCall: (calleeId: string, kind: CallKind) => Promise<string>;
}

function rowToEntry(row: RemoteCallHistoryRow): CallHistoryEntry {
  return {
    id: row.id,
    user: {
      id: row.peerAccountId,
      username: row.peerAccountId,
      displayName: row.peerDisplayName,
      ...(row.peerAvatarUri !== undefined ? { avatarUri: row.peerAvatarUri } : {}),
    },
    direction: row.direction,
    kind: row.kind,
    timestamp: row.timestamp,
    ...(row.durationSec !== undefined ? { durationSec: row.durationSec } : {}),
    ...(row.participants !== undefined ? { participants: row.participants } : {}),
  };
}

function entryToRow(entry: CallHistoryEntry, syncedAt: number): RemoteCallHistoryRow {
  return {
    id: entry.id,
    peerAccountId: entry.user.id,
    peerDisplayName: entry.user.displayName,
    ...(entry.user.avatarUri !== undefined ? { peerAvatarUri: entry.user.avatarUri } : {}),
    direction: entry.direction,
    kind: entry.kind,
    timestamp: entry.timestamp,
    ...(entry.durationSec !== undefined ? { durationSec: entry.durationSec } : {}),
    ...(entry.participants !== undefined
      ? { participants: entry.participants.map((p) => ({ ...p })) }
      : {}),
    syncedAt,
  };
}

async function syncToCache(signal?: AbortSignal): Promise<void> {
  const res = await loadCallHistory(signal ? { signal } : {});
  if (signal?.aborted) return;
  if (res.calls.length === 0) return;
  const now = Date.now();
  await db.remote_call_history.bulkPut(res.calls.map((e) => entryToRow(e, now)));
}

export function useCallHistory(): UseCallHistoryResult {
  const [isSyncing, setIsSyncing] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Reactive read — `undefined` during the brief initial async Dexie read,
  // then an array (possibly empty) once the query resolves.
  const rows = useLiveQuery(
    () => db.remote_call_history.orderBy('timestamp').reverse().limit(200).toArray(),
    [],
  );

  const calls = useMemo<readonly CallHistoryEntry[]>(
    () => (rows ?? []).map(rowToEntry),
    [rows],
  );

  const doSync = useCallback(async (signal?: AbortSignal) => {
    setIsSyncing(true);
    setError(null);
    try {
      await syncToCache(signal);
    } catch (err) {
      if (signal?.aborted) return;
      log.error({ event: 'calls.history.sync.failed', err: (err as Error).message });
      setError(err as Error);
    } finally {
      if (!signal?.aborted) setIsSyncing(false);
    }
  }, []);

  // Background sync on mount — writes to Dexie; liveQuery re-emits automatically.
  useEffect(() => {
    const c = new AbortController();
    void doSync(c.signal);
    return () => c.abort();
  }, [doSync]);

  const refresh = useCallback(async () => {
    await doSync();
  }, [doSync]);

  const startCall = useCallback(async (_calleeId: string, _kind: CallKind): Promise<string> => {
    // startCall is orchestrated by CallsRouteHost via callStore.startCall.
    // This stub satisfies the interface — actual call start does not go
    // through /v1/calls/start from history anymore.
    return '';
  }, []);

  const isLoading = rows === undefined || (rows.length === 0 && isSyncing);

  return { calls, isLoading, error, refresh, startCall };
}
